// ── GA4 Analytics — Central utility for PandaPro ───────────────────────────
// Loads Google Analytics asynchronously, tracks events/pageviews for SPA.
// All functions are silent no-ops when GA is disabled or not initialized.

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
    _ga_initialized: boolean;
  }
}

export interface AnalyticsConfig {
  enabled: boolean;
  measurementId: string;
  /** 'production' = only load on non-localhost | 'all' = always load */
  environment: 'production' | 'all';
  trackEvents: boolean;
}

let _config: AnalyticsConfig | null = null;
let _userId: string | null = null;
let _userPlan: string | null = null;

/** Update the user identity associated with subsequent events */
export function setAnalyticsUser(userId: string | null, plan: string | null): void {
  _userId = userId;
  _userPlan = plan;
  if (_config?.enabled && typeof window.gtag === 'function') {
    window.gtag('config', _config.measurementId, {
      user_id: userId ?? undefined,
    });
  }
}

/** Bootstrap GA4: inject script + initialize gtag. Call once at app startup. */
export function initAnalytics(config: AnalyticsConfig): void {
  if (!config.enabled || !config.measurementId) return;

  // Environment guard
  const host = window.location.hostname;
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
  if (config.environment === 'production' && isLocalhost) return;

  // Prevent double init
  if (window._ga_initialized) return;
  window._ga_initialized = true;

  _config = config;

  // Setup dataLayer + gtag function BEFORE the script loads
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', config.measurementId, {
    send_page_view: false, // We send page_view manually for SPA route changes
  });

  // Async script injection — non-blocking
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${config.measurementId}`;
  document.head.appendChild(script);
}

/** Track a virtual page view (call on every SPA route change) */
export function trackPageView(path: string, title?: string): void {
  if (!_config?.enabled || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    user_id: _userId ?? undefined,
  });
}

/** Track a named event with optional params.
 *  user_id, user_plan and timestamp are appended automatically.
 *  Nothing is sent if GA is disabled or trackEvents is false. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!_config?.enabled || !_config.trackEvents || typeof window.gtag !== 'function') return;
  window.gtag('event', name, {
    ...params,
    user_id: _userId ?? undefined,
    user_plan: _userPlan ?? undefined,
    timestamp: new Date().toISOString(),
  });
}

/** True when GA has been initialized and gtag is ready */
export function isAnalyticsReady(): boolean {
  return !!(_config?.enabled && typeof window.gtag === 'function');
}
